angular.module('bizmetryApp').service('BizInterceptorService', [
  '$http', '$q', 'CONFIG', 'ReferenceDataService',
  function ($http, $q, CONFIG, ReferenceDataService) {

    let listPauseCount = 0;
    const listRequests = new Set();
    this.isListRefreshPaused = function () { return listPauseCount > 0; };
    this.pauseListRefresh = function () {
      listPauseCount++;
      listRequests.forEach(function (cancel) { cancel.resolve(); });
      let released = false;
      return function () {
        if (released) return;
        released = true;
        listPauseCount = Math.max(0, listPauseCount - 1);
      };
    };

    const BASE = CONFIG.BIZMETRY_BACKEND_URL + '/v1/api/bizmetry-biz-events/biz-interceptor';

    // Interceptor transports — the infrastructure an interceptor captures over.
    // The catalog is reference data (BIZ_INTERCEPTOR_TRANSPORT_TYPE: label = the
    // transport_type wire value, desc = its description). Only REST_API is
    // implemented for now; the rest show as "Coming soon".
    var self = this;
    var ENABLED_TRANSPORTS = ['REST_API'];
    var TRANSPORT_ICONS = {
      REST_API: 'api', MQTT: 'sensors', WEBHOOK: 'webhook',
      KAFKA: 'hub', SFTP: 'dns', FILE_SYSTEM: 'folder_open'
    };

    // Display shape of one transport — needs no catalog, so list rows and the
    // edit wizard's badge can use it straight from a transport_type. Memoized:
    // the same id always yields the same object, so it's safe in watched
    // bindings (ng-if compares by identity; a fresh object each digest never settles).
    var _transportShapes = {};
    this.describeTransport = function (id, description) {
      id = id || '';
      var key = id + '|' + (description || '');
      return _transportShapes[key] || (_transportShapes[key] = {
        id:          id,
        label:       id.replace(/_/g, ' '),
        description: description || '',
        icon:        TRANSPORT_ICONS[id] || 'bolt',
        css:         id.toLowerCase().replace(/[^a-z0-9]/g, ''),
        enabled:     ENABLED_TRANSPORTS.indexOf(id) > -1
      });
    };

    var _transportTypes = null;   // cached promise — the catalog doesn't change at runtime
    this.getTransportTypes = function () {
      if (!_transportTypes) {
        _transportTypes = ReferenceDataService.getReferenceData('BIZ_INTERCEPTOR_TRANSPORT_TYPE')
          .then(function (response) {
            return (response.data || [])
              .sort(function (a, b) { return a.refObjectId - b.refObjectId; })
              .map(function (r) { return self.describeTransport(r.refObjectLabel, r.refObjectDesc); });
          })
          .catch(function (error) {
            _transportTypes = null;   // let the next caller retry
            throw error;
          });
      }
      return _transportTypes;
    };

    function getAccountId() {
      try {
        const user = JSON.parse(sessionStorage.getItem('user'));
        return user && user.id ? user.id : null;
      } catch (e) {
        return null;
      }
    }

    // The live token is stored under authTokenInfo (AuthService) and is only
    // attached to $http calls by AuthInterceptor — raw XHR (downloadArtifact)
    // bypasses that, so it has to read it from here itself.
    function getBearer() {
      try {
        const tokenInfo = JSON.parse(sessionStorage.getItem('authTokenInfo') || 'null');
        return (tokenInfo && tokenInfo.access_token) || null;
      } catch (e) {
        return null;
      }
    }

    function baseHeaders() {
      return { 'X-User-Account-Id': getAccountId() };
    }

    function authHeaders() {
      const h = baseHeaders();

      const defaultAuth = $http.defaults.headers.common &&
                          $http.defaults.headers.common['Authorization'];

      if (defaultAuth) {
        h['Authorization'] = defaultAuth;
      } else {
        const bearer = getBearer();
        if (bearer) h['Authorization'] = bearer.startsWith('Bearer ') ? bearer : `Bearer ${bearer}`;
      }

      return h;
    }

    this.batchCapacityCeiling = function (agent) {
      const frames = Number(agent.agentMetricsDiskMaxFrames);
      const forwarders = Number(agent.agentSafForwarderMaxCount);
      if (!Number.isSafeInteger(frames) || frames <= 0 || !Number.isInteger(forwarders) || forwarders <= 0) {
        throw new Error('Agent frame capacity and maximum SAF forwarders must be positive integers.');
      }
      const ceiling = Math.ceil(frames / forwarders) - 1;
      if (ceiling < 1) { throw new Error('Agent capacity must allow at least two frames per SAF forwarder.'); }
      return ceiling;
    };

    this.getBatchCapacityLimits = function (profile) {
      const service = this;
      const limits = {};
      return $q.all((profile.environments || []).map(function (env) {
        const agentId = env.agent_id;
        if (!agentId) { return; }
        return $http.get(CONFIG.BIZMETRY_BACKEND_URL + '/v1/api/bizmetry-agents/agents/' + agentId,
          { headers: baseHeaders() }).then(function (response) {
            limits[env.id] = service.batchCapacityCeiling(response.data);
          });
      })).then(function () { return limits; });
    };

    // ======================
    // LIST BY PROFILE
    // GET /?profile_id={profileId}&page=&size=&search=&sort_field=&sort_dir=
    // ======================
    this.listByProfile = function (profileId, options = {}) {
      if (options.pauseWithMetrics && listPauseCount) {
        return $q.reject({ status: -1, metricsPaused: true });
      }
      const params = {
        page: options.page !== undefined ? options.page : 0,
        size: options.size !== undefined ? options.size : 10
      };

      // Omitted entirely (not just set to null/undefined) when absent —
      // the backend treats a missing profile_id as "every profile in the
      // account" (see BizInterceptorController#getAllBizInterceptors),
      // which callers like the name-uniqueness pre-check rely on. Whether
      // a null-valued param key gets serialized away or sent as the
      // literal string "null" isn't worth depending on.
      if (profileId)          params.profile_id = profileId;
      if (options.search)    params.search      = options.search;
      if (options.sortField) params.sort_field  = options.sortField;
      if (options.sortDir)   params.sort_dir    = options.sortDir;
      if (options.liveOnly)  params.live_only   = true;
      if (options.transportType) params.transport_type = options.transportType;

      const config = { headers: authHeaders(), params };
      if (options.skipLoader) { config.bmSkipLoader = true; }
      if (!options.pauseWithMetrics) return $http.get(BASE, config);
      const cancel = $q.defer();
      config.timeout = cancel.promise;
      listRequests.add(cancel);
      return $http.get(BASE, config).finally(function () { listRequests.delete(cancel); });
    };

    // ======================
    // GET ONE
    // GET /{bizInterceptorId}
    // ======================
    // options.skipLoader suppresses the global LoadingHttpInterceptor spinner
    // (bmSkipLoader — see that interceptor's own isApiRequest/shouldSkip) —
    // used by ConsolidatedStatsDialog/EnvironmentStatusDialog's silent
    // background auto-refresh polling, which re-fetches this same
    // interceptor every few seconds and shouldn't flash a spinner each time.
    this.getById = function (bizInterceptorId, options) {
      options = options || {};
      var config = { headers: authHeaders() };
      if (options.skipLoader) { config.bmSkipLoader = true; }
      return $http.get(`${BASE}/${bizInterceptorId}`, config);
    };

    // ======================
    // CREATE
    // POST /
    // ======================
    this.create = function (payload) {
      return $http.post(
        BASE,
        payload,
        { headers: authHeaders() }
      );
    };

    // ======================
    // CLONE — "Copy of <name>" with the same configuration (created disabled)
    // POST /{bizInterceptorId}/clone
    // ======================
    this.clone = function (bizInterceptorId) {
      return $http.post(
        `${BASE}/${bizInterceptorId}/clone`,
        null,
        { headers: authHeaders() }
      );
    };

    // ======================
    // PATCH
    // PATCH /{bizInterceptorId}
    // ======================
    this.patch = function (bizInterceptorId, payload) {
      return $http.patch(
        `${BASE}/${bizInterceptorId}`,
        payload,
        { headers: authHeaders() }
      );
    };

    // ======================
    // RESET LIFETIME STATS — Consolidated Stats' "Reset All"
    // POST /{bizInterceptorId}/reset-stats[?environment_id=...]
    // Omitting environmentId resets every environment's Lifetime Totals;
    // passing one resets only that environment's.
    // ======================
    this.resetLifetimeStats = function (bizInterceptorId, environmentId) {
      var url = `${BASE}/${bizInterceptorId}/reset-stats`;
      if (environmentId) { url += `?environment_id=${encodeURIComponent(environmentId)}`; }
      return $http.post(url, null, { headers: authHeaders() });
    };

    // ======================
    // RESET DISCOVERY STATS — Consolidated Stats' Discovery "Reset All"
    // POST /{bizInterceptorId}/reset-discovery-stats[?environment_id=...]
    // Omitting environmentId restarts the discovery capture of every
    // environment (General tab); passing one restarts only that environment's.
    // ======================
    this.resetDiscoveryStats = function (bizInterceptorId, environmentId) {
      var url = `${BASE}/${bizInterceptorId}/reset-discovery-stats`;
      if (environmentId) { url += `?environment_id=${encodeURIComponent(environmentId)}`; }
      return $http.post(url, null, { headers: authHeaders() });
    };

    // ======================
    // RESET OPERATION STATS — Consolidated Stats' Operations "Reset All"
    // POST /{bizInterceptorId}/reset-operation-stats[?environment_id=...]
    // Omitting environmentId empties every environment's per-operation
    // breakdown (General tab); passing one empties only that environment's.
    // ======================
    this.resetOperationStats = function (bizInterceptorId, environmentId) {
      var url = `${BASE}/${bizInterceptorId}/reset-operation-stats`;
      if (environmentId) { url += `?environment_id=${encodeURIComponent(environmentId)}`; }
      return $http.post(url, null, { headers: authHeaders() });
    };

    // ======================
    // DELETE
    // DELETE /{bizInterceptorId}
    // ======================
    this.delete = function (bizInterceptorId) {
      return $http.delete(
        `${BASE}/${bizInterceptorId}`,
        { headers: authHeaders() }
      );
    };

    // ======================
    // BULK DELETE — "Delete All" of the selected interceptors
    // DELETE /byIds  {"biz_interceptor_ids": [...]}
    // ======================
    this.bulkDeleteByIds = function (bizInterceptorIds) {
      return $http({
        method: 'DELETE',
        url: `${BASE}/byIds`,
        data: { biz_interceptor_ids: bizInterceptorIds },
        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders())
      });
    };

    // ======================
    // DOWNLOAD SELECTED — the selected interceptors' artifacts for one environment
    // in one zip, a folder per interceptor. Binary: plain XHR + arraybuffer, like
    // downloadArtifact below.
    // POST /download-bundle  {"biz_interceptor_ids": [...], "environment_id": "…"}
    // ======================
    this.downloadBundle = function (bizInterceptorIds, environmentId) {
      const deferred = $q.defer();
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${BASE}/download-bundle`, true);
      xhr.responseType = 'arraybuffer';
      xhr.setRequestHeader('Content-Type', 'application/json');

      const bearer = getBearer();
      if (bearer) {
        xhr.setRequestHeader('Authorization', bearer.startsWith('Bearer ') ? bearer : `Bearer ${bearer}`);
      }
      const accountId = getAccountId();
      if (accountId) {
        xhr.setRequestHeader('X-User-Account-Id', accountId);
      }

      xhr.onload = function () {
        if (xhr.status === 200) {
          deferred.resolve(xhr.response);
          return;
        }
        let message = `Download failed with status ${xhr.status}`;
        try {
          const body = JSON.parse(new TextDecoder().decode(xhr.response));
          if (body && body.message) { message = body.message; }
        } catch (e) { /* not a JSON error body */ }
        deferred.reject(xhr.status === 401 ? 'Unauthorized: your session may have expired.' : message);
      };
      xhr.onerror = function () {
        deferred.reject('Download failed due to network error');
      };

      xhr.send(JSON.stringify({ biz_interceptor_ids: bizInterceptorIds, environment_id: environmentId }));
      return deferred.promise;
    };

    // ======================
    // DOWNLOAD ARTIFACT — the complete interceptor SDK for one environment,
    // as a zip (bizmetry-interceptor.properties injected into it server-side
    // — inside the artifact itself when its format supports that, e.g. a
    // JAR, or as a sibling file in the zip otherwise; the agent's TLS cert
    // is NOT part of this — the interceptor fetches that live on its own at
    // init/sync instead). Binary, streamed from the backend — plain XHR +
    // arraybuffer rather than $http, same reasoning as
    // TelemetryAgentService#downloadAgentWithProgress (angular $http
    // buffers/transforms the body in ways that don't suit a raw binary
    // payload this size).
    // GET /{bizInterceptorId}/environment/{environmentId}/download-artifact
    // ======================
    this.downloadArtifact = function (bizInterceptorId, environmentId) {
      const deferred = $q.defer();
      const url = `${BASE}/${bizInterceptorId}/environment/${environmentId}/download-artifact`;

      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'arraybuffer';

      const bearer = getBearer();
      if (bearer) {
        xhr.setRequestHeader('Authorization', bearer.startsWith('Bearer ') ? bearer : `Bearer ${bearer}`);
      }
      const accountId = getAccountId();
      if (accountId) {
        xhr.setRequestHeader('X-User-Account-Id', accountId);
      }

      xhr.onload = function () {
        if (xhr.status === 200) {
          // The server sends its own self-describing filename via
          // Content-Disposition, but that header isn't in this API's
          // Access-Control-Expose-Headers (a Kong-level CORS setting, not
          // something this service controls) — cross-origin browsers hide
          // unlisted response headers from JS entirely, so
          // getResponseHeader('Content-Disposition') would just return null
          // here. The caller builds the same-shaped filename itself instead
          // (see BizInterceptorsTabController#downloadArtifact).
          deferred.resolve(xhr.response);
        } else if (xhr.status === 401) {
          deferred.reject('Unauthorized: your session may have expired.');
        } else {
          deferred.reject(`Download failed with status ${xhr.status}`);
        }
      };
      xhr.onerror = function () {
        deferred.reject('Download failed due to network error');
      };

      xhr.send();
      return deferred.promise;
    };

    // ======================
    // METRICS EXPLORER — time-series data captured on each /sync call,
    // aggregated (avg/max/min per dimension) into fixed-size windows.
    // environmentId is optional — omit it (null/undefined) to aggregate
    // across every environment of this interceptor.
    // GET /{bizInterceptorId}/time-series?environment_id=&start_ts=&end_ts=&interval_seconds=&page=&size=
    // ======================
    // skipLoader (trailing, optional) suppresses the global
    // LoadingHttpInterceptor spinner — see getById's own comment. Metrics
    // Explorer's Live Monitoring tick passes true here so its periodic
    // background re-fetch doesn't flash the global spinner; regular
    // user-initiated calls (pagination, filter/interval changes) leave it
    // at the default false and keep the spinner.
    this.getAggregatedTimeSeries = function (bizInterceptorId, environmentId, startTs, endTs, intervalSeconds, page, size, skipLoader) {
      const params = {
        start_ts: startTs,
        end_ts: endTs,
        interval_seconds: intervalSeconds || 900,
        page: page !== undefined ? page : 0,
        size: size || 20
      };
      if (environmentId) params.environment_id = environmentId;

      const config = { headers: authHeaders(), params };
      if (skipLoader) { config.bmSkipLoader = true; }
      return $http.get(`${BASE}/${bizInterceptorId}/time-series`, config);
    };

    // ======================
    // METRICS EXPLORER — time range available for this interceptor's
    // time-series data (optionally scoped to one environment).
    // GET /{bizInterceptorId}/time-series/time-range?environment_id=
    // ======================
    // skipLoader — see getAggregatedTimeSeries's own comment just above.
    this.getTimeSeriesTimeRange = function (bizInterceptorId, environmentId, skipLoader) {
      const params = {};
      if (environmentId) params.environment_id = environmentId;

      const config = { headers: authHeaders(), params };
      if (skipLoader) { config.bmSkipLoader = true; }
      return $http.get(`${BASE}/${bizInterceptorId}/time-series/time-range`, config);
    };

  }
]);
